// src/components/messages/ChatInput.js

import React, { useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { reportCaughtError } from '../../utils/clientErrorReporting';
import { isRecoverableFirestoreAssertion } from '../../utils/firestoreAssertionRecovery';
import {
  discardMediaUpload,
  uploadModeratedMedia,
  validateMediaAttachmentFiles,
} from '../../utils/mediaUploads';
import { offlineWriteMessage, useOnlineStatus } from '../../utils/onlineStatus';
import { getDirectMessageGifRating, sendDirectMessage } from '../../utils/messages';
import { notifyFilePickerClose, notifyFilePickerOpen } from '../../utils/filePickerActivity';
import GifPicker from '../gifs/GifPicker';
import ComposerMediaRail from './ComposerMediaRail';

const MEDIA_BUSY_STATUSES = new Set(['preparing', 'uploading', 'awaiting_upload', 'scanning']);
const MEDIA_DRAFT_STORAGE_PREFIX = 'memberNest.directMessageMediaDraft.v1';
const MEDIA_DRAFT_UPDATED_EVENT = 'membernest:direct-media-draft-updated';

function mediaDraftStorageKey(threadId, senderId) {
  return `${MEDIA_DRAFT_STORAGE_PREFIX}:${encodeURIComponent(threadId || '')}:${encodeURIComponent(senderId || '')}`;
}

function readPersistedMediaDraft(threadId, senderId) {
  if (typeof window === 'undefined' || !threadId || !senderId) return [];
  try {
    const value = JSON.parse(window.sessionStorage.getItem(mediaDraftStorageKey(threadId, senderId)) || '[]');
    return (Array.isArray(value) ? value : [])
      .filter((media) => media?.provider === 'firebase_upload' && media?.uploadId && media?.storagePath)
      .map((media) => ({
        id: `restored-${media.uploadId}`,
        fileName: media.fileName || (media.mediaType === 'video' ? 'Video' : 'Image'),
        status: 'approved',
        progress: 100,
        media,
        error: '',
      }));
  } catch (err) {
    return [];
  }
}

function persistApprovedMediaDraft(threadId, senderId, media) {
  if (typeof window === 'undefined' || !threadId || !senderId || !media?.uploadId) return;
  const key = mediaDraftStorageKey(threadId, senderId);
  const current = readPersistedMediaDraft(threadId, senderId).map((item) => item.media);
  const next = [...current.filter((entry) => entry.uploadId !== media.uploadId), media];
  try {
    window.sessionStorage.setItem(key, JSON.stringify(next));
    window.dispatchEvent(new CustomEvent(MEDIA_DRAFT_UPDATED_EVENT, { detail: { key } }));
  } catch (err) {}
}

function removePersistedMediaDraft(threadId, senderId, uploadId = '') {
  if (typeof window === 'undefined' || !threadId || !senderId) return;
  const key = mediaDraftStorageKey(threadId, senderId);
  try {
    if (!uploadId) {
      window.sessionStorage.removeItem(key);
      return;
    }
    const next = readPersistedMediaDraft(threadId, senderId)
      .map((item) => item.media)
      .filter((media) => media.uploadId !== uploadId);
    if (next.length > 0) window.sessionStorage.setItem(key, JSON.stringify(next));
    else window.sessionStorage.removeItem(key);
  } catch (err) {}
}

function mergePersistedMediaItems(current, restored) {
  const uploadIds = new Set(current.map((item) => item.media?.uploadId).filter(Boolean));
  return [...current, ...restored.filter((item) => !uploadIds.has(item.media?.uploadId))];
}

function draftItemId(file, index) {
  return `${Date.now()}-${index}-${file.name}-${file.size}`;
}

function mediaStatusDetail(status, progress) {
  if (status === 'uploading') return `${progress}% complete`;
  if (status === 'preparing') return 'Preparing upload';
  if (status === 'error') return 'Could not attach this media';
  return 'Finalising upload';
}

/**
 * ChatInput
 *
 * Renders the input box and Send button.
 * Handles sending a new message, updating thread metadata,
 * and creating notifications for other participants.
 *
 * Props:
 * - threadId: ID of the current thread
 * - senderId: UID of the current user
 * - participants: Array of all participant UIDs
 * - userProfiles: Map of UID to { firstName, lastName }
 */
export default function ChatInput({ threadId, senderId, participants, userProfiles, routeContext = {} }) {
  const [value, setValue] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const [popoutOpen, setPopoutOpen] = useState(false);
  const [gifPickerOpen, setGifPickerOpen] = useState(false);
  const [selectedGif, setSelectedGif] = useState(null);
  const [mediaItems, setMediaItems] = useState(() => readPersistedMediaDraft(threadId, senderId));
  const [gifRating, setGifRating] = useState('g');
  const textareaRef = useRef(null);
  const popoutTextareaRef = useRef(null);
  const fileInputRef = useRef(null);
  const online = useOnlineStatus();
  const mediaBusy = mediaItems.some((item) => MEDIA_BUSY_STATUSES.has(item.status));
  const mediaAttachments = mediaItems.map((item) => item.media).filter(Boolean);
  const hasMediaItems = mediaItems.length > 0;

  useEffect(() => {
    const key = mediaDraftStorageKey(threadId, senderId);
    const handleDraftUpdated = (event) => {
      if (event.detail?.key !== key) return;
      const restored = readPersistedMediaDraft(threadId, senderId);
      setMediaItems((current) => mergePersistedMediaItems(current, restored));
    };
    window.addEventListener(MEDIA_DRAFT_UPDATED_EVENT, handleDraftUpdated);
    return () => window.removeEventListener(MEDIA_DRAFT_UPDATED_EVENT, handleDraftUpdated);
  }, [senderId, threadId]);

  const discardDraftMedia = useCallback((media) => {
    if (media?.provider !== 'firebase_upload' || !media?.uploadId) return;
    discardMediaUpload(media.uploadId).catch((err) => {
      reportCaughtError(err, {
        source: 'messages.direct.mediaDiscard',
        severity: 'warning',
        context: { threadId },
      });
    });
  }, [threadId]);

  const discardDraftMediaItems = useCallback((items = []) => {
    items.forEach((item) => discardDraftMedia(item?.media || item));
  }, [discardDraftMedia]);

  useEffect(() => {
    let cancelled = false;
    if (!threadId || !online) {
      setGifRating('g');
      return undefined;
    }
    getDirectMessageGifRating(threadId)
      .then((result) => {
        if (!cancelled) setGifRating(result?.rating || 'g');
      })
      .catch(() => {
        if (!cancelled) setGifRating('g');
      });
    return () => {
      cancelled = true;
    };
  }, [online, threadId]);

  const resizeTextarea = useCallback((el) => {
    if (!el) return;
    const maxHeight = parseFloat(window.getComputedStyle(el).maxHeight) || Math.round(window.innerHeight * 0.35);
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight, maxHeight)}px`;
  }, []);

  const resizeEditors = useCallback(() => {
    resizeTextarea(textareaRef.current);
    resizeTextarea(popoutTextareaRef.current);
  }, [resizeTextarea]);

  useEffect(() => {
    window.requestAnimationFrame(resizeEditors);
  }, [value, popoutOpen, resizeEditors]);

  useEffect(() => {
    window.addEventListener('resize', resizeEditors);
    return () => window.removeEventListener('resize', resizeEditors);
  }, [resizeEditors]);

  const handleSend = async () => {
    const text = value.trim();
    if ((!text && !selectedGif && mediaAttachments.length === 0) || sending || mediaBusy || !threadId) return;
    if (!online) {
      setError(offlineWriteMessage('send messages'));
      return;
    }

    setError('');
    setSending(true);
    try {
      await sendDirectMessage({
        threadId,
        content: text,
        media: selectedGif,
        mediaAttachments,
      });
      setValue('');
      setSelectedGif(null);
      setMediaItems([]);
      removePersistedMediaDraft(threadId, senderId);
      setPopoutOpen(false);
      setGifPickerOpen(false);
    } catch (err) {
      setError('Could not send this message. Please refresh and try again.');
      if (isRecoverableFirestoreAssertion(err)) return;
      reportCaughtError(err, {
        source: 'messages.direct.sendMessage',
        severity: 'warning',
        context: {
          threadId,
          senderId,
          participantCount: Array.isArray(participants) ? participants.length : 0,
          ...routeContext,
        },
      });
    } finally {
      setSending(false);
    }
  };

  const handleClearDraft = () => {
    setValue('');
    setSelectedGif(null);
    discardDraftMediaItems(mediaItems);
    setMediaItems([]);
    removePersistedMediaDraft(threadId, senderId);
    window.requestAnimationFrame(resizeEditors);
  };

  const handleChooseFile = async (event) => {
    notifyFilePickerClose();
    const files = Array.from(event.target.files || []).filter(Boolean);
    event.target.value = '';
    if (files.length === 0 || !threadId || sending || !online || mediaBusy) return;

    setError('');
    try {
      validateMediaAttachmentFiles(files, mediaAttachments);
    } catch (err) {
      setError(err?.message || 'Could not attach this media.');
      return;
    }

    const draftItems = files.map((file, index) => ({
      id: draftItemId(file, index),
      fileName: file.name,
      status: 'preparing',
      progress: 0,
      media: null,
      error: '',
    }));
    setMediaItems((current) => [...current, ...draftItems]);

    for (let index = 0; index < files.length; index += 1) {
      const file = files[index];
      const itemId = draftItems[index].id;
      try {
        const media = await uploadModeratedMedia({
          file,
          surface: 'direct_message',
          target: { threadId },
          onProgress: (progress) => setMediaItems((current) => current.map((item) => (
            item.id === itemId ? { ...item, progress } : item
          ))),
          onStateChange: ({ status }) => setMediaItems((current) => current.map((item) => (
            item.id === itemId ? { ...item, status } : item
          ))),
        });
        setMediaItems((current) => current.map((item) => (
          item.id === itemId ? { ...item, media, status: 'approved', progress: 100, error: '' } : item
        )));
        persistApprovedMediaDraft(threadId, senderId, media);
      } catch (err) {
        setMediaItems((current) => current.map((item) => (
          item.id === itemId ? { ...item, status: 'error', error: err?.message || 'Could not attach this media.' } : item
        )));
        setError(err?.message || 'Could not attach this media.');
        reportCaughtError(err, {
          source: 'messages.direct.mediaUpload',
          severity: 'warning',
          context: {
            threadId,
            senderId,
            participantCount: Array.isArray(participants) ? participants.length : 0,
            ...routeContext,
          },
        });
      }
    }
  };

  const removeMediaItem = (itemId) => {
    setMediaItems((current) => {
      const item = current.find((entry) => entry.id === itemId);
      if (item?.media) {
        discardDraftMedia(item.media);
        removePersistedMediaDraft(threadId, senderId, item.media.uploadId);
      }
      return current.filter((entry) => entry.id !== itemId);
    });
  };

  const renderMediaItems = () => (
    hasMediaItems && (
      <div className="chat-input__media-preview">
        <ComposerMediaRail
          items={mediaItems}
          onRemove={removeMediaItem}
          removeButtonClassName="chat-input__media-remove"
          busyStatuses={MEDIA_BUSY_STATUSES}
          statusDetail={mediaStatusDetail}
        />
      </div>
    )
  );

  const hasSendableContent = Boolean(value.trim() || selectedGif || mediaAttachments.length > 0);

  return (
    <div className="chat-input">
      <div className="chat-input__editor">
        <textarea
          ref={textareaRef}
          value={value}
          onChange={e => setValue(e.target.value)}
          onKeyDown={e => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              handleSend();
            }
          }}
          placeholder="Type a message..."
          rows={1}
          disabled={sending || !online}
        />
        {selectedGif && (
          <div className="chat-input__gif-preview">
            <img src={selectedGif.previewUrl || selectedGif.url} alt={selectedGif.title || 'Selected GIF'} />
            <div className="chat-input__gif-preview-body">
              <strong>GIF attached</strong>
              <span>{selectedGif.title || 'GIPHY GIF'}</span>
            </div>
            <button type="button" onClick={() => setSelectedGif(null)} aria-label="Remove selected GIF">
              <svg aria-hidden="true" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
                <path d="M18 6 6 18" />
                <path d="m6 6 12 12" />
              </svg>
            </button>
          </div>
        )}
        {renderMediaItems()}
        <div className="chat-input__actions">
          <div className="chat-input__tools">
            <input
              ref={fileInputRef}
              type="file"
              accept="image/jpeg,image/png,image/webp,video/mp4,video/quicktime,video/webm"
              multiple
              onChange={handleChooseFile}
              hidden
            />
            <button
              type="button"
              className="chat-input__tool"
              onClick={() => {
                notifyFilePickerOpen();
                fileInputRef.current?.click();
              }}
              disabled={!threadId || sending || mediaBusy || !online}
              aria-label="Attach photo or video"
              title="Attach photo or video"
            >
              <svg aria-hidden="true" width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21.44 11.05 12 20.49a6 6 0 0 1-8.49-8.49l9.44-9.44a4 4 0 0 1 5.66 5.66l-9.44 9.44a2 2 0 0 1-2.83-2.83l8.49-8.49" />
              </svg>
            </button>
            <button
              type="button"
              className="chat-input__tool"
              onClick={() => setGifPickerOpen((current) => !current)}
              disabled={!threadId || sending || mediaBusy || !online}
              aria-label="Choose GIF"
              title="Choose GIF"
            >
              GIF
            </button>
            <button
              type="button"
              className="chat-input__tool"
              onClick={() => setPopoutOpen(true)}
              disabled={!threadId || sending || !online}
              aria-label="Open larger composer"
              title="Open larger composer"
            >
              <svg aria-hidden="true" width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M15 3h6v6" />
                <path d="M10 14 21 3" />
                <path d="M9 21H3v-6" />
                <path d="M14 10 3 21" />
              </svg>
            </button>
            <button
              type="button"
              className="chat-input__tool"
              onClick={handleClearDraft}
              disabled={(!value.trim() && !selectedGif && !hasMediaItems) || sending || mediaBusy}
              aria-label="Clear draft"
              title="Clear draft"
            >
              <svg aria-hidden="true" width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M3 6h18" />
                <path d="M8 6V4h8v2" />
                <path d="M19 6l-1 14H6L5 6" />
                <path d="M10 11v5" />
                <path d="M14 11v5" />
              </svg>
            </button>
          </div>
          <button type="button" className="chat-input__send" onClick={handleSend} disabled={!hasSendableContent || sending || mediaBusy || !online}>
            {sending ? 'Sending...' : 'Send'}
          </button>
        </div>
      </div>
      <GifPicker
        open={gifPickerOpen}
        rating={gifRating}
        surface="directMessages"
        onSelect={(gif) => {
          setSelectedGif(gif);
          setGifPickerOpen(false);
        }}
        onClose={() => setGifPickerOpen(false)}
        disabled={!online}
      />
      {!online && <p className="text-muted" style={{ margin: 0 }}>Reconnect to send messages.</p>}
      {error && <p className="text-danger" style={{ margin: 0 }}>{error}</p>}

      {popoutOpen && (
        <div className="chat-compose-popout" role="presentation" onMouseDown={() => setPopoutOpen(false)}>
          <div className="chat-compose-popout__panel" role="dialog" aria-modal="true" aria-label="Compose message" onMouseDown={(event) => event.stopPropagation()}>
            <div className="chat-compose-popout__header">
              <h2>Compose message</h2>
              <button type="button" onClick={() => setPopoutOpen(false)} aria-label="Close composer">×</button>
            </div>
            <textarea
              ref={popoutTextareaRef}
              value={value}
              onChange={e => setValue(e.target.value)}
              onKeyDown={e => {
                if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
                  e.preventDefault();
                  handleSend();
                }
              }}
              placeholder="Type a message..."
              autoFocus
              disabled={sending || !online}
            />
            <div className="chat-compose-popout__footer">
              <span>Use Enter for a new line.</span>
              <button type="button" onClick={handleSend} disabled={!hasSendableContent || sending || mediaBusy || !online}>
                {sending ? 'Sending...' : 'Send'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

ChatInput.propTypes = {
  threadId: PropTypes.string.isRequired,
  senderId: PropTypes.string.isRequired,
  participants: PropTypes.arrayOf(PropTypes.string).isRequired,
  userProfiles: PropTypes.objectOf(
    PropTypes.shape({
      firstName: PropTypes.string,
      lastName: PropTypes.string
    })
  ).isRequired,
  routeContext: PropTypes.object
};
